import { Button, Stack, Text } from "flicket-ui";
import { useRouter } from "next/router";
import { useContext, useMemo, useState } from "react";
import CustomModal from "~components/CustomModal";
import { ModalContext } from "~components/CustomModal/useCustomModal";
import { Order } from "~graphql/sdk";
import { useSDK } from "~hooks";
import { useTimer } from "~hooks/checkout/useTimer";
import { useOrganization } from "~hooks/useOrganization";
import { handlePromise } from "~lib";
import { HighDemandModalTicketCard } from "./HighDemandModalTicketCard";
import { LockingInPriceSpinner } from "./LockingInPriceSpinner";

export interface HighDemandModalContentAffectedTicket {
  lineItemId: string;
  name: string;
  price: number;
  fee: number;
  quantity: number;
  customBadge?: {
    label: string;
    textColor: string;
    backgroundColor: string;
  };
}

interface HighDemandModalContentProps {
  affectedTickets: HighDemandModalContentAffectedTicket[];
  order: Pick<Order, "id" | "expiresInSeconds">;
  showFees: boolean;
}

export function HighDemandModalContent({
  affectedTickets,
  order,
  showFees,
}: HighDemandModalContentProps) {
  const router = useRouter();
  const sdk = useSDK();

  const { close } = useContext(ModalContext);

  const { organization } = useOrganization();

  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [isLockingInPrice, setIsLockingInPrice] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const expiresInSeconds = useMemo(
    // One minute less to reduce the risk of the order timing out before the
    // timer expires due to desync, high network latency, etc..
    () => ({ value: order ? order.expiresInSeconds - 60 : -1 }),
    [order]
  );

  async function handleLockInPrice() {
    setIsLoading(true);
    setIsLockingInPrice(true);

    await handlePromise(async () =>
      sdk.extendOrderExpiry({ orderId: order.id })
    );

    setIsLoading(false);
  }

  function handleClose() {
    // Send background request to expire the order. We ignore any errors as
    // there isn't anything that the user can do if one occurs, and we expect
    // the mutation to error if the order has already expired.
    void handlePromise(async () => sdk.expireOrder({ id: order.id }));

    close();
  }

  function handleExpiry() {
    setHasTimedOut(true);
  }

  if (isLockingInPrice) {
    return (
      <LockingInPriceSpinner
        isLoading={isLoading}
        onAnimationComplete={() => {
          void router.push("/checkout/[orderId]", `/checkout/${order.id}`);
        }}
      />
    );
  }

  return (
    <>
      <CustomModal.Header onClose={handleClose}>
        <Stack
          direction="horizontal"
          alignItems="center"
          gap={2}
          justifyContent="space-between"
        >
          <Text variant="header.M">
            {hasTimedOut
              ? "Tickets no longer available"
              : "There's a lot of demand!"}
          </Text>
          <Text
            variant="header.XS"
            fontWeight="bold"
            color="error"
            textAlign="right"
            flex={1}
            paddingRight={{ _: 0, sm: 2 }}
          >
            <Timer
              expiresInSeconds={expiresInSeconds}
              onExpiry={handleExpiry}
            />
          </Text>
        </Stack>
      </CustomModal.Header>
      <CustomModal.Content>
        <Stack direction="vertical" gap={3}>
          {hasTimedOut && (
            <Text variant="regular">
              Your tickets were released due to high demand.
              <br />
              <br />
              Ticket availability changes quickly, so the ones you selected are
              no longer reserved. You can return to the ticket selection page to
              check the current availability.
            </Text>
          )}
          {!hasTimedOut && (
            <>
              <Text variant="regular">
                The price for your selected tickets has increased due to demand.
                We&apos;ve found the next best available option for you &ndash;
                if you change your selection you risk losing this price!
              </Text>
              {affectedTickets.map((affectedTicket) => (
                <HighDemandModalTicketCard
                  ticket={affectedTicket}
                  showFees={showFees}
                  currency={organization.currency}
                  key={affectedTicket.lineItemId}
                />
              ))}
            </>
          )}
        </Stack>
      </CustomModal.Content>
      <CustomModal.Footer>
        <Stack
          direction="horizontal"
          gap={1}
          justifyContent="flex-end"
          flexWrap="wrap"
        >
          <Button
            variant={hasTimedOut ? "primary" : "outline"}
            onClick={handleClose}
          >
            {hasTimedOut ? "Return to tickets" : "Change selection"}
          </Button>
          {!hasTimedOut && (
            <Button variant="primary" onClick={() => void handleLockInPrice()}>
              Lock in this price
            </Button>
          )}
        </Stack>
      </CustomModal.Footer>
    </>
  );
}

interface TimerProps {
  expiresInSeconds: { value: number };
  onExpiry: () => void;
}

function Timer({ expiresInSeconds, onExpiry }: TimerProps) {
  const timer = useTimer({
    expiresInSeconds,
    onExpiry,
  });

  const minutesLeft = Math.floor(timer.secondsRemaining / 60);
  const secondsLeft = timer.secondsRemaining % 60;

  return (
    <>
      <span css={{ textWrap: "nowrap" }}>Time left</span> {minutesLeft}:
      {secondsLeft < 10 && "0"}
      {secondsLeft}
    </>
  );
}
